// src/components/public/WebinarSponsorshipModal.js
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const SPONSORSHIP_TIERS = [
    {
        id: 'individual',
        name: 'Individual / Business',
        price: 'Any Amount',
        coverage: 'Open to everyone',
        color: '#2E86AB',
        bgColor: '#EFF8FF',
        borderColor: '#2E86AB',
        stripeUrl: 'https://buy.stripe.com/9B6eVcdzVgbU0PKgZS77O0d',
        benefits: [
            'Support ARCA webinars as an individual or small business',
            'Contribute any amount that fits your budget',
        ],
    },
    {
        id: 'bronze',
        name: 'Bronze',
        price: '$500 USD',
        coverage: 'Support one webinar',
        color: '#CD7F32',
        bgColor: '#FFF8F0',
        borderColor: '#CD7F32',
        stripeUrl: 'https://buy.stripe.com/fZubJ08fBbVEaqkeRK77O09',
        benefits: [
            "Logo placement on the selected webinar's promotional flyer",
            "Verbal recognition during the webinar's opening and closing",
            'Sponsor listing on the ARCA webinar webpage',
            'One social-media appreciation post',
        ],
    },
    {
        id: 'silver',
        name: 'Silver',
        price: '$1,000 USD',
        coverage: 'Support up to two webinars',
        color: '#A8A9AD',
        bgColor: '#F8F9FA',
        borderColor: '#A8A9AD',
        stripeUrl: 'https://buy.stripe.com/7sYbJ067t9Nwbuo9xq77O0a',
        benefits: [
            'All Bronze Sponsorship benefits',
            'Logo placement on promotional materials for up to two webinars',
            'Up to a 30-second sponsor acknowledgment during each sponsored session',
            "Two social-media features with the sponsor's link",
            'Logo included in selected event communications',
        ],
    },
    {
        id: 'gold',
        name: 'Gold',
        price: '$1,500 USD',
        coverage: 'Support up to four webinars',
        color: '#D4AF37',
        bgColor: '#FFFDF0',
        borderColor: '#D4AF37',
        stripeUrl: 'https://buy.stripe.com/4gMbJ03Zle3M1TOeRK77O0c',
        benefits: [
            'All Silver Sponsorship benefits',
            'Prominent logo placement on webinar-series marketing materials',
            'Up to a 60-second sponsor message during selected sessions',
            'Three dedicated social-media features',
            'One digital product or service spotlight',
        ],
    },
    {
        id: 'platinum',
        name: 'Platinum',
        price: '$2,000 USD',
        coverage: 'Sponsor the full webinar series',
        color: '#5B5EA6',
        bgColor: '#F5F5FF',
        borderColor: '#5B5EA6',
        stripeUrl: 'https://buy.stripe.com/5kQeVc67t8Js7e8cJC77O0b',
        benefits: [
            'Premier "Webinar Series Sponsor" recognition',
            'Top-tier logo placement across webinar-series materials',
            'Up to a 90-second sponsor message during selected sessions',
            'Dedicated sponsor feature or interview',
            'Recognition in ARCA email and media communications',
            'Post-series impact acknowledgment',
        ],
    },
];

const WebinarSponsorshipModal = ({ show, onClose }) => {
    const [selected, setSelected] = useState(null);
    const [showPayment, setShowPayment] = useState(false);

    if (!show) return null;

    const handleClose = () => {
        setShowPayment(false);
        setSelected(null);
        onClose();
    };

    const overlayStyle = {
        position: 'fixed', inset: 0, zIndex: 9999,
        backgroundColor: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
    };

    const modalStyle = {
        background: '#fff',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '860px',
        maxHeight: '92vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
    };

    const headerStyle = {
        background: 'linear-gradient(135deg, #6B8E23 0%, #4A6B18 100%)',
        padding: '24px 28px',
        borderRadius: '12px 12px 0 0',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    };

    const closeBtnStyle = {
        background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
        width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer',
        fontSize: '1.2rem', lineHeight: '32px', textAlign: 'center', flexShrink: 0,
    };

    // ── PAYMENT SUMMARY VIEW ──
    if (showPayment && selected) {
        return createPortal(
            <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
                <div style={{ ...modalStyle, maxWidth: '520px' }}>

                    {/* Header */}
                    <div style={{ ...headerStyle, padding: '18px 24px', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                Webinar Sponsorship — Secure Checkout
                            </div>
                            <h2 style={{ margin: '4px 0 0', fontSize: '1.25rem', fontWeight: 700 }}>
                                {selected.name} Package — {selected.price}
                            </h2>
                        </div>
                        <button onClick={handleClose} style={closeBtnStyle}>×</button>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '28px', textAlign: 'center' }}>

                        {/* Tier badge */}
                        <div style={{
                            display: 'inline-block',
                            background: selected.color,
                            color: '#fff',
                            borderRadius: '24px',
                            padding: '6px 20px',
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            marginBottom: '16px',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                        }}>
                            {selected.name} Sponsor
                        </div>

                        <div style={{ fontSize: '2.4rem', fontWeight: 800, color: '#1f2937', marginBottom: '4px' }}>
                            {selected.price}
                        </div>
                        <div style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.95rem' }}>
                            {selected.coverage}
                        </div>

                        {/* Benefits */}
                        <div style={{
                            background: selected.bgColor,
                            border: `1px solid ${selected.borderColor}`,
                            borderRadius: '10px',
                            padding: '16px 20px',
                            textAlign: 'left',
                            marginBottom: '28px',
                        }}>
                            <div style={{ fontWeight: 700, color: selected.color, marginBottom: '10px', fontSize: '0.9rem' }}>
                                What you get:
                            </div>
                            <ul style={{ paddingLeft: '18px', margin: 0, fontSize: '0.875rem', color: '#374151' }}>
                                {selected.benefits.map((b, i) => (
                                    <li key={i} style={{ marginBottom: '6px' }}>{b}</li>
                                ))}
                            </ul>
                        </div>

                        {/* Secure badge */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '8px', color: '#6b7280', fontSize: '0.82rem', marginBottom: '20px',
                        }}>
                            <span>🔒</span>
                            <span>Payments are processed securely by Stripe</span>
                        </div>

                        {/* Pay button — opens Stripe in same tab */}
                        <a
                            href={selected.stripeUrl}
                            rel="noopener noreferrer"
                            style={{
                                display: 'block',
                                background: `linear-gradient(135deg, ${selected.color} 0%, ${selected.borderColor} 100%)`,
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                padding: '16px 40px',
                                borderRadius: '10px',
                                textDecoration: 'none',
                                boxShadow: `0 4px 18px ${selected.color}55`,
                                letterSpacing: '0.3px',
                                transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                        >
                            Proceed to Secure Payment{selected.price !== 'Any Amount' ? ` — ${selected.price}` : ''}
                        </a>

                        <p style={{ color: '#9ca3af', fontSize: '0.78rem', marginTop: '10px' }}>
                            Clicking above will take you to Stripe's secure payment page.
                        </p>
                    </div>

                    {/* Back */}
                    <div style={{ padding: '12px 24px', borderTop: '1px solid #f3f4f6' }}>
                        <button
                            onClick={() => setShowPayment(false)}
                            style={{
                                background: '#f3f4f6', border: 'none', borderRadius: '8px',
                                padding: '8px 20px', cursor: 'pointer', fontWeight: 600,
                                color: '#374151', fontSize: '0.9rem',
                            }}
                        >
                            ← Back to Packages
                        </button>
                    </div>

                </div>
            </div>
            , document.body);
    }

    // ── TIER SELECTION VIEW ──
    return createPortal(
        <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
            <div style={modalStyle}>

                {/* Header */}
                <div style={headerStyle}>
                    <div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.85, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                            ARCA Webinar Series
                        </div>
                        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Sponsorship Packages</h2>
                        <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                            Choose a sponsorship tier and make an impact on respiratory care across Africa
                        </p>
                    </div>
                    <button onClick={handleClose} style={closeBtnStyle}>×</button>
                </div>

                {/* Disclaimer */}
                <div style={{ padding: '16px 28px 0' }}>
                    <div style={{
                        background: '#fff5f5',
                        border: '2px solid #c53030',
                        borderRadius: '8px',
                        padding: '16px 20px',
                        color: '#c53030',
                        fontSize: '1rem',
                        fontWeight: 700,
                        lineHeight: 1.5,
                        textAlign: 'center',
                    }}>
                        DISCLAIMER: All webinar presenters VOLUNTEER their time and receive NO compensation. Sponsorship proceeds support ARCA's educational programming, webinar delivery, continuing education activities, communications, and respiratory-care initiatives across Africa.
                    </div>
                </div>

                {/* Tier Cards */}
                <div style={{ padding: '16px 28px 0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
                        {SPONSORSHIP_TIERS.map((tier) => (
                            <div
                                key={tier.id}
                                onClick={() => setSelected(tier)}
                                style={{
                                    border: `2px solid ${selected && selected.id === tier.id ? tier.color : '#e5e7eb'}`,
                                    borderRadius: '10px',
                                    padding: '16px',
                                    cursor: 'pointer',
                                    background: selected && selected.id === tier.id ? tier.bgColor : '#fff',
                                    transition: 'all 0.2s',
                                    boxShadow: selected && selected.id === tier.id ? `0 4px 16px ${tier.color}40` : '0 1px 4px rgba(0,0,0,0.08)',
                                    position: 'relative',
                                }}
                            >
                                <div style={{
                                    display: 'inline-block',
                                    background: tier.color,
                                    color: '#fff',
                                    borderRadius: '20px',
                                    padding: '3px 12px',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    marginBottom: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                }}>
                                    {tier.name}
                                </div>

                                {selected && selected.id === tier.id && (
                                    <div style={{
                                        position: 'absolute', top: '10px', right: '10px',
                                        background: tier.color, color: '#fff',
                                        borderRadius: '50%', width: '20px', height: '20px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.7rem',
                                    }}>✓</div>
                                )}

                                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', marginBottom: '4px' }}>
                                    {tier.price}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '12px' }}>
                                    {tier.coverage}
                                </div>

                                <ul style={{ paddingLeft: '14px', margin: 0, fontSize: '0.78rem', color: '#374151' }}>
                                    {tier.benefits.slice(0, 3).map((b, i) => (
                                        <li key={i} style={{ marginBottom: '4px' }}>{b}</li>
                                    ))}
                                    {tier.benefits.length > 3 && (
                                        <li style={{ color: tier.color, fontWeight: 600 }}>
                                            +{tier.benefits.length - 3} more benefit{tier.benefits.length - 3 > 1 ? 's' : ''}
                                        </li>
                                    )}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Selected tier full benefits */}
                {selected && (
                    <div style={{ padding: '20px 28px 0' }}>
                        <div style={{
                            background: selected.bgColor,
                            border: `1px solid ${selected.borderColor}`,
                            borderRadius: '8px',
                            padding: '16px',
                        }}>
                            <h4 style={{ margin: '0 0 10px', color: selected.color, fontSize: '1rem' }}>
                                {selected.name} Sponsorship — Full Benefits
                            </h4>
                            <ul style={{ paddingLeft: '18px', margin: 0, fontSize: '0.875rem', color: '#374151' }}>
                                {selected.benefits.map((b, i) => (
                                    <li key={i} style={{ marginBottom: '6px' }}>{b}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    padding: '20px 28px 24px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    borderTop: '1px solid #f3f4f6',
                    marginTop: '20px',
                }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280', maxWidth: '400px' }}>
                        <em>Every Breath Matters. Every Partner Counts.</em><br />
                        Payments are processed securely via Stripe.
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={handleClose}
                            style={{
                                background: '#f3f4f6', border: 'none', borderRadius: '8px',
                                padding: '10px 22px', cursor: 'pointer', fontWeight: 600, color: '#374151',
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => { if (selected) setShowPayment(true); }}
                            disabled={!selected}
                            style={{
                                background: selected ? '#6B8E23' : '#9ca3af',
                                border: 'none', borderRadius: '8px',
                                padding: '10px 28px', cursor: selected ? 'pointer' : 'not-allowed',
                                fontWeight: 700, color: '#fff', fontSize: '0.95rem',
                                transition: 'background 0.2s',
                            }}
                        >
                            {selected ? `Continue — ${selected.name}${selected.price !== 'Any Amount' ? ` · ${selected.price}` : ''}` : 'Select a Tier to Continue'}
                        </button>
                    </div>
                </div>

            </div>
        </div>
        , document.body);
};

// Standalone trigger button — import and use wherever needed
export const WebinarSponsorshipButton = ({ label = 'Sponsor Our Webinars', style = {} }) => {
    const [showModal, setShowModal] = useState(false);
    return (
        <>
            <button
                onClick={() => setShowModal(true)}
                style={{
                    background: '#C0392B',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    boxShadow: '0 4px 14px rgba(192,57,43,0.4)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'transform 0.2s, box-shadow 0.2s, background 0.2s',
                    ...style,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.background = '#A93226';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(192,57,43,0.55)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.background = '#C0392B';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(192,57,43,0.4)';
                }}
            >
                <span>🎙️</span>
                {label}
            </button>
            <WebinarSponsorshipModal show={showModal} onClose={() => setShowModal(false)} />
        </>
    );
};

export default WebinarSponsorshipModal;
